import React from 'react';
import ServicePageLayout from '../../components/seo/ServicePageLayout';
import { getGallery } from '../../seo/galleryConfig';

const faqs = [
  {
    q: 'My toilet runs continuously — is that an easy fix?',
    a: 'Usually yes. A continuously running toilet is normally a worn flapper, a stuck flush valve, or a faulty fill valve in the cistern. Most are sub-£50 parts and can be replaced in 30 minutes. The water saving alone often pays for the repair within a few weeks.'
  },
  {
    q: 'My toilet wobbles or rocks — is that dangerous?',
    a: 'It is not immediately dangerous but it will get worse. A wobbling pan usually means the floor fixings have failed, the wax seal underneath is broken, or the floor itself is rotting (a sign of an old leak). All three need attention before the seal fails and water starts leaking under the floor with every flush.'
  },
  {
    q: 'Can you install a new toilet for me?',
    a: 'Absolutely. Whether you are upgrading to a slimline modern unit, adding a downstairs cloakroom, or replacing a damaged pan, we supply and install both close-coupled and wall-hung systems. Existing pipework is usually adaptable; we will inspect and quote on the visit.'
  },
  {
    q: 'How long does a toilet replacement take?',
    a: 'A straightforward like-for-like swap usually takes 2-3 hours including testing. Cloakroom installations involving new feed and waste pipework take 4-6 hours. We discuss timing on the quote so you are not left without a toilet for longer than necessary.'
  },
  {
    q: 'Do you also handle Saniflo / macerator units?',
    a: 'Yes — we service and replace macerator units in cloakrooms and basements. These need slightly more frequent maintenance than gravity systems but can be a great solution where conventional drainage isn\'t possible.'
  },
];

const relatedServices = [
  { slug: 'plumbing', label: 'Plumbing Services', blurb: 'Full domestic plumbing across Kent, London and Essex.' },
  { slug: 'bathroom-plumbing', label: 'Bathroom Plumbing', blurb: 'Coming soon — full bathroom installs and renovations.' },
  { slug: 'blocked-drains', label: 'Blocked Drains', blurb: 'When the toilet is the symptom of a deeper drain problem.' },
];

const ToiletRepairsPage = () => (
  <ServicePageLayout
    seoTitle="Toilet Repairs & Installation Services in Kent, London & Essex | H.A.R.P Solutions LTD"
    seoDescription="Toilet repair and installation services across Kent, London and Essex. Cistern repairs, flush issues, leaking pans, full replacements. Same-week appointments. Call +44 330 122 1094."
    canonicalPath="/services/toilet-repairs"
    keywords="toilet repair, toilet not flushing, leaking toilet, toilet installation, cistern repair, toilet repair Kent, toilet repair London, toilet repair Essex"
    pageTitle="Toilet Repairs & Installations — Fast, Fair, Done Properly"
    serviceName="Toilet Repairs &amp; Installations"
    serviceSlug="toilet-repairs"
    heroSubheading="Flush problems, leaks, replacements — fixed in one visit."
    intro="A toilet that doesn't flush properly is a small daily annoyance that quickly becomes a big problem. A toilet that leaks at the base damages floors silently for months before you notice. We fix every kind of toilet issue across Kent, London and Essex — from a 30-minute cistern adjustment to a full pan and cistern replacement."
    bullets={[
      'Cistern, flush valve and fill valve repairs',
      'Leaking pan and wax seal replacement',
      'Loose or wobbly toilet refixing',
      'Full close-coupled and wall-hung installations',
      'Cloakroom and downstairs WC installs',
      'Saniflo / macerator service and replacement',
    ]}
    faqs={faqs}
    relatedServices={relatedServices}
    galleryImages={getGallery('toilet-repairs')}
  >
    <h2>Common Toilet Problems We Fix</h2>
    <h3>Continuous running</h3>
    <p>
      Almost always a worn flapper valve, a misaligned fill valve, or a kinked refill tube. A continuously running toilet wastes about
      200–400 litres of water per day — that's £20-£40 a month on your water bill. We replace the offending part with a quality
      manufacturer's component (not the cheapest plastic), test, and check the rest of the cistern while we're there.
    </p>
    <h3>Weak or partial flush</h3>
    <p>
      Caused by either a partially-closed inlet valve, scale build-up in the flush mechanism, or a partial blockage in the rim feed.
      We diagnose properly rather than just replacing parts and hoping.
    </p>
    <h3>Leak at the base</h3>
    <p>
      Almost always a failed wax seal between the pan and the soil pipe. Sometimes a cracked pan or a failed floor fixing. We lift the
      pan, replace the wax seal with a modern silicone replacement, refix securely, and re-seal the floor join with sanitary silicone.
    </p>
    <h3>Wobbly pan</h3>
    <p>
      The floor fixings have worked loose, the wax seal is failing, or the floor below the toilet is rotting from years of slow seepage.
      We assess all three and fix what's needed in one visit.
    </p>
    <h3>Cracked pan or damaged cistern</h3>
    <p>
      Hairline cracks in ceramic are not safely repairable long-term — replacement is the right answer. We carry common close-coupled
      models or can source any modern replacement on quick supplier orders.
    </p>

    <h2>Toilet Installations</h2>
    <p>
      Replacing an existing toilet is one of the highest-value bathroom upgrades. A modern dual-flush, slimline pan instantly modernises
      the room, reduces water usage significantly, and removes the visual clutter of old chrome cisterns. We supply and fit a full range
      of styles: traditional close-coupled, modern back-to-wall, wall-hung concealed-cistern, and macerator-fed cloakroom units.
    </p>
    <p>
      For new downstairs cloakrooms (a popular extension idea adding meaningful resale value), we handle the full plumbing build — soil
      pipe extension, basin feed and waste, isolation valves, and final pan fitting.
    </p>

    <h2>How To Book</h2>
    <p>
      Call <a href="tel:+443301221094"><strong>+44 330 122 1094</strong></a> with a quick description of the issue (or send a video on
      WhatsApp using the green button at the bottom of the page) and we will give you a same-call estimate. For a 24/7 emergency see our{' '}
      <a href="/services/emergency-plumber">emergency plumber service</a>.
    </p>
  </ServicePageLayout>
);

export default ToiletRepairsPage;
